// Base partagée : les collections de l'ERP vivent dans MongoDB (backend) et
// localStorage ne sert plus que de cache local. Chaque écriture locale est
// poussée au serveur ; un sondage régulier ramène les changements des autres
// postes (version attendue : un poste périmé ne remplace pas une version récente).
import { accepterEpoch, chargerEtatDemarrage, ecrituresFermees } from './demarrage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CLES = [
  'oliva.tracabilite.config',
  'oliva.reprises_depart',
  'oliva.reprises_huile',
  'oliva.receptions.v3',
  'oliva.portail.tokens',
  'oliva.cuves',
  'oliva.transferts',
  'oliva.depenses',
  'oliva.financements',
  'oliva.avances',
  'oliva.contacts',
  'oliva.finances',
  'oliva.ventes',
  'oliva.sousproduits',
  'oliva.formats',
  'oliva.emballages',
  'oliva.appro_emballages',
  'oliva.conditionnements',
  'oliva.produits_finis',
  'oliva.factures_libres',
  'oliva.pointages',
  'oliva.operations_coursier',
  'oliva.ofs',
  'oliva.utilisateurs',
  'oliva.souches',
  'oliva.numeros',
  'oliva.tarifs',
  'oliva.validation',
  'oliva.branding',
  'oliva.audit',
  'oliva.portail',
  'oliva.achats_huile',
  'oliva.bonus',
  'oliva.retraits',
  'oliva.conversions',
  'oliva.rapprochement',
  'oliva.brouillons',
  'oliva.standards_industriels',
  'oliva.charges_industrielles',
  'oliva.imputations_industrielles_of',
  'oliva.regularisations_industrielles',
];

const versions = {};
let enAttente = new Map();
let minuteur = null;
let ecouteurs = [];
let ecouteursEcriture = [];
export const surSync = (fn) => {
  ecouteurs.push(fn);
  return () => {
    ecouteurs = ecouteurs.filter((f) => f !== fn);
  };
};
// Notifié après chaque écriture serveur réussie avec la liste des clés modifiées.
export const surEcriture = (fn) => {
  ecouteursEcriture.push(fn);
};

const brut = {
  get: localStorage.getItem.bind(localStorage),
  set: localStorage.setItem.bind(localStorage),
  remove: localStorage.removeItem.bind(localStorage),
};

// Écriture strictement locale (cache), sans publication vers la base partagée :
// réservée aux jeux de démonstration, pour qu'un poste vierge n'écrase JAMAIS le
// référentiel réel du serveur (contacts, fournisseurs…).
export const ecrireLocalSeulement = (cle, valeur) => brut.set(cle, valeur);

export let etatSync = { connecte: false, derniere: null };

// Chargement initial de la base serveur. RÈGLE DE SÉCURITÉ DES DONNÉES : tant que
// l'état n'est pas « ok », AUCUNE écriture locale n'est publiée vers le serveur —
// un cache vide (poste neuf) ou périmé ne peut donc jamais écraser la base. En cas
// d'échec de lecture, l'interface l'affiche et les enregistrements restent bloqués.
export let etatHydratation = { etat: 'en_cours', erreur: null, session: true, ignorees: 0 };
let ecouteursHydratation = [];
export const surHydratation = (fn) => {
  ecouteursHydratation.push(fn);
  fn(etatHydratation);
  return () => {
    ecouteursHydratation = ecouteursHydratation.filter((f) => f !== fn);
  };
};
const annoncerHydratation = () => ecouteursHydratation.forEach((f) => f(etatHydratation));
export const donneesPretes = () => etatHydratation.etat === 'ok';

let ecrituresEnCours = 0;
let conflitFinances = false;
// Lecture seule : empêcher un export de partir avant la persistance des saisies du poste.
export const pretPourExportFiscal = () => donneesPretes() && !enAttente.size && !ecrituresEnCours;
async function pousser(cle, valeur, strict = false) {
  ecrituresEnCours += 1;
  try {
  const r = await fetch(`${API}/store/${cle}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: JSON.parse(valeur), version: versions[cle] ?? 0 }),
  });
  // 403 : collection hors du périmètre de ce profil. JAMAIS absorbé (audit C02) —
  // l'erreur remonte et l'interface signale une modification non enregistrée.
  if (r.status === 403) {
    const refus = await r.json().catch(() => ({}));
    const e = new Error(refus.detail ? `403 — ${refus.detail}` : `PUT ${cle} → 403`);
    e.statut = 403;
    throw e;
  }
  if (!r.ok) {
    if (r.status === 503) {
      // Maintenance déclarée pendant que cet écran était ouvert : l'interface bascule
      // immédiatement au lieu d'annoncer une panne d'écriture.
      const refus = await r.clone().json().catch(() => ({}));
      if (refus.maintenance) chargerEtatDemarrage();
    }
    if (cle === 'oliva.finances' && r.status === 409) {
      const refus = await r.json().catch(() => ({}));
      conflitFinances = true;
      // Conserver le brouillon refusé pour récupération, sans le republier.
      brut.set('oliva.finances.saisie_refusee', brut.get(cle) || valeur);
      etatHydratation = { etat: 'echec', session: true, ignorees: 1,
        erreur: `${refus.detail || 'Modification financière refusée.'} Votre saisie reste conservée localement. Réessayez le chargement serveur avant de continuer.` };
      annoncerHydratation();
    }
    const detail = await r.json().catch(() => ({}));
    const e = new Error(detail.detail ? `${r.status} — ${detail.detail}` : `PUT ${cle} → ${r.status}`);
    e.statut = r.status;
    throw e;
  }
  const { version, data } = await r.json();
  versions[cle] = version;
  // Le serveur fait autorité sur ce qu'il a réellement écrit : règlements immuables
  // réinsérés, tarifs normalisés, décision de dérogation du minimum de trituration
  // horodatée et signée côté serveur. Le cache adopte sa copie — sinon le poste
  // republierait sa propre version au coup suivant et se ferait refuser (409).
  // Ne jamais écraser une modification locale survenue pendant la requête.
  if (data && brut.get(cle) === valeur) {
    brut.set(cle, JSON.stringify(data));
    window.dispatchEvent(new Event('store-maj'));
  }
  } finally { ecrituresEnCours -= 1; }
}

// ÉCRITURE CRITIQUE (trésorerie). La file d'attente normale est optimiste : elle
// confirme l'écriture locale avant toute réponse du serveur et ravale les échecs
// dans un réessai en mémoire, perdu au prochain rechargement. Pour un mouvement de
// caisse c'est inacceptable : l'argent disparaîtrait en silence. Cette fonction
// publie la clé immédiatement, remonte l'erreur serveur à l'appelant, et remet le
// cache local dans son état d'avant pour qu'aucune écriture fantôme ne subsiste.
export async function publierMaintenant(cle, avant) {
  if (!donneesPretes())
    throw new Error('Base serveur non chargée : enregistrement impossible, aucune écriture n’a été faite.');
  const valeur = enAttente.get(cle) ?? brut.get(cle);
  if (valeur === null || valeur === undefined) return;
  enAttente.delete(cle);
  try {
    await pousser(cle, valeur, true);
  } catch (e) {
    brut.set(`saisie-non-confirmee:${cle}`, brut.get(cle) ?? valeur);
    etatHydratation = { etat: 'echec', session: true, ignorees: enAttente.size + 1,
      erreur: `Écriture NON CONFIRMÉE (${cle}) : ${String(e.message || e)}. Vérifiez la base avant toute nouvelle saisie. Brouillon conservé sur ce poste.` };
    annoncerHydratation();
    if (avant === null || avant === undefined) brut.remove(cle);
    else brut.set(cle, avant);
    enAttente.delete(cle);
    etatSync = { ...etatSync, connecte: false };
    ecouteurs.forEach((f) => f(etatSync));
    window.dispatchEvent(new Event('store-maj'));
    throw e;
  }
  tracer(cle);
  etatSync = { connecte: true, derniere: new Date() };
  ecouteurs.forEach((f) => f(etatSync));
  ecouteursEcriture.forEach((f) => f([cle]));
  // Les autres clés en attente gardent leur publication différée normale.
  if (enAttente.size) {
    clearTimeout(minuteur);
    minuteur = setTimeout(viderFile, 400);
  }
}

// Relecture serveur du registre financier : preuve de lecture après une écriture
// critique. Un commit annoncé par le serveur n'est pas une ligne relisible par les
// postes — l'appelant compare lui-même la ligne attendue avant de marquer un bon.
export async function lireFinancesServeur() {
  const r = await fetch(`${API}/store/oliva.finances`);
  if (!r.ok) throw new Error(`GET oliva.finances → ${r.status}`);
  return r.json();
}

// La copie serveur fait foi : le cache local est remplacé, sans republication.
export function imposerFinances(data, version) {
  enAttente.delete('oliva.finances');
  brut.set('oliva.finances', JSON.stringify(data));
  if (version !== undefined) versions['oliva.finances'] = version;
  window.dispatchEvent(new Event('store-maj'));
}

// Aptitude réelle de la base à recevoir une écriture de trésorerie (transaction).
export async function santeFinances() {
  const r = await fetch(`${API}/store/finances/sante`);
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ? `${r.status} — ${d.detail}` : `GET finances/sante → ${r.status}`);
  }
  return r.json();
}

export async function actualiserFinancesServeur() {
  if (enAttente.size || ecrituresEnCours) return false;
  const r = await fetch(`${API}/store/oliva.finances`);
  if (!r.ok) return false;
  const d = await r.json();
  if (enAttente.size || ecrituresEnCours) return false;
  brut.set('oliva.finances', JSON.stringify(d.data));
  versions['oliva.finances'] = d.version;
  window.dispatchEvent(new Event('store-maj'));
  return true;
}

// Journal multi-postes : identifiant de poste stable par navigateur.
const POSTE_KEY = 'oliva.poste';
export const posteId = (() => {
  let p = brut.get(POSTE_KEY);
  if (!p) {
    p = `POSTE-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    brut.set(POSTE_KEY, p);
  }
  return p;
})();

const tracer = (cle) => {
  if (cle === POSTE_KEY) return;
  // L'auteur est déduit de la session serveur : rien n'est déclaré par le poste.
  fetch(`${API}/activite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      poste: posteId,
      collection: cle,
      action: 'enregistrement',
    }),
  }).catch(() => {});
};

async function viderFile() {
  // Filet de sécurité : rien n'est publié avant la fin du chargement serveur,
  // ni pendant une fenêtre de maintenance (remise à zéro en cours).
  if (!donneesPretes() || ecrituresFermees()) return;
  const lot = enAttente;
  enAttente = new Map();
  const reussies = [];
  for (const [cle, valeur] of lot) {
    try {
      await pousser(cle, valeur);
      tracer(cle);
      reussies.push(cle);
      etatSync = { connecte: true, derniere: new Date() };
    } catch (e) {
      etatSync = { ...etatSync, connecte: false };
      // Une ancienne requête refusée ne remplace jamais une saisie plus récente.
      const brouillon = enAttente.get(cle) ?? brut.get(cle) ?? valeur;
      brut.set(`saisie-non-confirmee:${cle}`, brouillon);
      if (!enAttente.has(cle)) enAttente.set(cle, brouillon);
      // Conserver AUSSI les autres collections non encore envoyées du lot.
      for (const [k, v] of lot) {
        if (reussies.includes(k)) continue;
        const recente = enAttente.get(k) ?? brut.get(k) ?? v;
        if (!enAttente.has(k)) enAttente.set(k, recente);
        brut.set(`saisie-non-confirmee:${k}`, recente);
      }
      etatHydratation = { etat: 'echec', session: true, ignorees: enAttente.size,
        erreur: `Écriture NON CONFIRMÉE (${cle}) : ${String(e.message || e)}. Brouillon conservé sur ce poste ; aucun réessai automatique.` };
      annoncerHydratation();
      break;
    }
  }
  if (reussies.length) ecouteursEcriture.forEach((f) => f(reussies));
  ecouteurs.forEach((f) => f(etatSync));
}

export async function fetchActivite() {
  const r = await fetch(`${API}/activite`);
  return r.ok ? r.json() : [];
}

export async function fetchSauvegardes() {
  const r = await fetch(`${API}/sauvegardes`);
  return r.ok ? r.json() : [];
}

export async function creerSauvegarde() {
  const r = await fetch(`${API}/sauvegardes`, { method: 'POST' });
  if (!r.ok) throw new Error('Sauvegarde impossible');
  return r.json();
}

export async function telechargerSauvegarde() {
  const r = await fetch(`${API}/sauvegardes/export`);
  if (!r.ok) throw new Error('Export impossible');
  const nom = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'sauvegarde-huilerie.json';
  const url = URL.createObjectURL(await r.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: nom });
  a.click();
  URL.revokeObjectURL(url);
  return nom;
}

export async function importerSauvegarde(fichier) {
  let doc;
  try {
    doc = JSON.parse(await fichier.text());
  } catch {
    throw new Error('Fichier illisible : ce n’est pas une sauvegarde JSON');
  }
  const r = await fetch(`${API}/sauvegardes/importer`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(doc) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Import impossible');
  return r.json();
}

export async function restaurerSauvegarde(id) {
  const r = await fetch(`${API}/sauvegardes/${id}/restaurer`, { method: 'POST' });
  if (!r.ok) throw new Error('Restauration impossible');
  return r.json();
}

// Référentiel : export / import entre environnements (aperçu ↔ production).
export async function exporterReferentiel() {
  const r = await fetch(`${API}/store/referentiel/export`);
  if (!r.ok) throw new Error('Export du référentiel impossible');
  const doc = await r.json();
  const url = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }));
  const nom = `referentiel-huilerie-${new Date().toISOString().slice(0, 10)}.json`;
  Object.assign(document.createElement('a'), { href: url, download: nom }).click();
  URL.revokeObjectURL(url);
  return { nom, ...doc };
}

export async function importerReferentiel(fichier, mode = 'fusion') {
  let doc;
  try {
    doc = JSON.parse(await fichier.text());
  } catch {
    throw new Error('Fichier illisible : ce n’est pas un export de référentiel JSON');
  }
  const contenu = doc.contenu ?? doc;
  const r = await fetch(`${API}/store/referentiel/importer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contenu, mode }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || 'Import du référentiel impossible');
  CLES_REFERENTIEL.forEach((c) => brut.remove(c));
  return data;
}

// Registre financier : JAMAIS publié depuis le cache d'un poste (audit C01/C06).
// Les écritures de trésorerie passent exclusivement par les commandes serveur
// (`lib/financeCommandes.js` → POST /api/finances/commande) ; le cache local n'est
// plus qu'une copie de lecture imposée par le serveur.
export const CLES_NON_PUBLIEES = ['oliva.finances'];

// Interception des écritures locales sur les clés métier.
localStorage.setItem = (cle, valeur) => {
  const metier = CLES.includes(cle) && !CLES_NON_PUBLIEES.includes(cle);
  if (metier && ecrituresFermees()) {
    // Maintenance de démarrage : la saisie n'est NI publiée NI mise en cache, sinon
    // elle repartirait vers le serveur à la réouverture.
    etatHydratation = { ...etatHydratation, ignorees: etatHydratation.ignorees + 1 };
    annoncerHydratation();
    return;
  }
  brut.set(cle, valeur);
  if (!metier) return;
  if (!donneesPretes()) {
    // Données serveur non chargées : l'écriture reste locale et n'est PAS publiée.
    // Le compteur remonte à l'interface, qui avertit l'utilisateur.
    etatHydratation = { ...etatHydratation, ignorees: etatHydratation.ignorees + 1 };
    annoncerHydratation();
    return;
  }
  enAttente.set(cle, valeur);
  clearTimeout(minuteur);
  minuteur = setTimeout(viderFile, 400);
};

// Chargement initial : la base serveur écrase le cache local.
export async function hydrater() {
  if (ecrituresEnCours) throw new Error('Une écriture est encore en cours ; attendez sa réponse avant de recharger.');
  for (const [cle, valeur] of enAttente) brut.set(`saisie-non-confirmee:${cle}`, valeur);
  if (conflitFinances && brut.get('oliva.finances')) {
    brut.set('oliva.finances.saisie_refusee', brut.get('oliva.finances'));
  }
  etatHydratation = { ...etatHydratation, etat: 'en_cours', erreur: null, session: true };
  annoncerHydratation();
  // Jeu de données renouvelé (remise à zéro) : le cache de ce poste est périmé et
  // ne doit rien republier. Il est effacé AVANT la lecture du serveur.
  const demarrage = await chargerEtatDemarrage();
  if (demarrage.renouvele) {
    enAttente.clear();
    conflitFinances = false;
    CLES.forEach((c) => {
      brut.remove(c);
      versions[c] = 0;
    });
    Object.keys(localStorage)
      .filter((k) => k.startsWith('saisie-non-confirmee:') || k === 'oliva.finances.saisie_refusee')
      .forEach((k) => brut.remove(k));
    accepterEpoch(demarrage.epoch);
  }
  try {
    const r = await fetch(`${API}/store`);
    if (!r.ok) throw new Error(`GET store → ${r.status}`);
    const distant = await r.json();
    Object.entries(distant).forEach(([cle, { data, version }]) => {
      if (data === null || data === undefined) return;
      brut.set(cle, JSON.stringify(data));
      versions[cle] = version;
    });
    if (conflitFinances) {
      enAttente.delete('oliva.finances');
      conflitFinances = false;
    }
    // AUDIT C11 : la base serveur fait autorité, y compris quand elle est VIDE.
    // Aucune republication d'un cache local absent du serveur (un poste périmé ou
    // un jeu de démonstration ne peut plus repeupler une base remise à zéro) et le
    // cache des collections absentes côté serveur est effacé.
    CLES.filter((c) => !(c in distant)).forEach((c) => { brut.remove(c); versions[c] = 0; });
    enAttente.clear();
    etatHydratation = { etat: 'ok', erreur: null, session: true, ignorees: 0 };
    annoncerHydratation();
    etatSync = { connecte: true, derniere: new Date() };
    return { connecte: true, cles: Object.keys(distant).length };
  } catch (e) {
    const message = String(e.message ?? e);
    etatSync = { connecte: false, derniere: null };
    // 401 = simplement « pas encore connecté » : ce n'est pas une panne à afficher.
    etatHydratation = { etat: 'echec', erreur: message, session: !message.includes('401'), ignorees: etatHydratation.ignorees };
    annoncerHydratation();
    return { connecte: false, erreur: message };
  }
}

// Sondage multi-postes. Un seul minuteur, jamais deux cycles en parallèle, et
// SEULES les collections dont la version a bougé sont relues (au lieu de
// retélécharger toute la base à chaque changement). L'onglet masqué ne sonde pas ;
// au retour, le cycle repart immédiatement. Après une erreur, le rythme ralentit
// progressivement puis revient au rythme normal dès la première réussite.
// Protection des saisies : aucune lecture n'est appliquée tant qu'une écriture est
// en cours ou en attente, et une version plus ancienne ne remplace jamais le cache.
export function demarrerSync(intervalle = 20000, maxErreur = 120000) {
  let minuteurSync = null;
  let enCours = false;
  let delai = intervalle;

  const planifier = (ms) => {
    clearTimeout(minuteurSync);
    minuteurSync = setTimeout(cycle, ms);
  };

  const lireCle = async (cle) => {
    const r = await fetch(`${API}/store/${cle}`);
    if (!r.ok) return false;
    const d = await r.json();
    if (enAttente.has(cle) || ecrituresEnCours) return false;
    if (!(d.version > (versions[cle] ?? 0))) return false;
    brut.set(cle, JSON.stringify(d.data));
    versions[cle] = d.version;
    return true;
  };

  async function cycle() {
    if (enCours || document.hidden) return;
    enCours = true;
    try {
      // Fenêtre de maintenance : relue à chaque cycle pour qu'un poste déjà ouvert
      // affiche le bandeau et cesse d'accepter des saisies sans rechargement.
      chargerEtatDemarrage();
      if (enAttente.size || ecrituresEnCours) return;
      const r = await fetch(`${API}/store/versions`);
      if (!r.ok) throw new Error(`GET versions → ${r.status}`);
      const distantes = await r.json();
      const changees = Object.keys(distantes).filter(
        (cle) => versions[cle] !== undefined && distantes[cle] > versions[cle]
      );
      etatSync = { connecte: true, derniere: new Date() };
      ecouteurs.forEach((f) => f(etatSync));
      delai = intervalle;
      let majFaite = false;
      // Concurrence bornée : au plus 3 lectures ciblées en parallèle.
      for (let i = 0; i < changees.length; i += 3) {
        const lot = await Promise.all(changees.slice(i, i + 3).map(lireCle));
        if (lot.some(Boolean)) majFaite = true;
      }
      if (majFaite) window.dispatchEvent(new Event('store-maj'));
    } catch {
      etatSync = { ...etatSync, connecte: false };
      ecouteurs.forEach((f) => f(etatSync));
      delai = Math.min(delai * 2, maxErreur);
    } finally {
      enCours = false;
      if (!document.hidden) planifier(delai);
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(minuteurSync);
      return;
    }
    delai = intervalle;
    planifier(0);
  });
  planifier(intervalle);
}

// RÈGLE D'ARCHITECTURE : données de référentiel, strictement persistantes.
// Aucune réinitialisation (démo, base propre) ni initialisation de campagne ne les efface.
export const CLES_REFERENTIEL = [
  'oliva.contacts',
  'oliva.formats',
  'oliva.tarifs',
  'oliva.souches',
  'oliva.utilisateurs',
  'oliva.branding',
  // Comptabilité analytique industrielle : barèmes, charges incorporées,
  // imputations figées et régularisations — jamais effacés par un reset.
  'oliva.standards_industriels',
  'oliva.charges_industrielles',
  'oliva.imputations_industrielles_of',
  'oliva.regularisations_industrielles',
];

// Jeu de démo : vide les données transactionnelles de la base partagée et du cache
// local, puis recharge. Le référentiel (contacts, catalogue, tarifs, paramètres) reste intact.
export async function reinitialiserDemo() {
  const r = await fetch(`${API}/store/reinitialiser-demo`, { method: 'POST' });
  if (!r.ok) throw new Error('Réinitialisation impossible');
  CLES.filter((c) => !CLES_REFERENTIEL.includes(c)).forEach((c) => brut.remove(c));
  return r.json();
}

// Réinitialisation propre : les clés transactionnelles reçoivent leur état vide ; le
// référentiel et la configuration sont conservés ; le serveur sauvegarde l'état courant avant.
export async function reinitialiserPropre(contenu) {
  const r = await fetch(`${API}/store/reinitialiser-propre`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contenu }),
  });
  if (!r.ok) throw new Error('Réinitialisation impossible');
  Object.entries(contenu)
    .filter(([cle]) => !CLES_REFERENTIEL.includes(cle))
    .forEach(([cle, data]) => brut.set(cle, JSON.stringify(data)));
  return r.json();
}
